import { useState, useRef, useEffect, useCallback } from "react";
import { Television, X, ArrowsOut, ArrowsIn, Play, Pause, SpeakerHigh, SpeakerSlash, Screencast, ArrowSquareOut, Horse, Timer, MapPin } from "@phosphor-icons/react";

const STREAM_SOURCES = {
  "ITV": {
    embed: "https://www.itv.com/watch/simulcast/itv",
    label: "ITV Racing",
    free: true,
    color: "#00A1E0",
  },
  "ITV4": {
    embed: "https://www.itv.com/watch/simulcast/itv4",
    label: "ITV4 Racing",
    free: true,
    color: "#00A1E0",
  },
  "Sky Sports Racing": {
    embed: "https://www.skysports.com/watch/sky-sports-racing",
    label: "Sky Sports Racing",
    free: false,
    color: "#E10600",
  },
  "Racing TV": {
    embed: "https://www.racingtv.com/live",
    label: "Racing TV",
    free: false,
    color: "#1B365D",
  },
};

export default function LiveStreamPlayer({ race, onClose }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [streamLoaded, setStreamLoaded] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [castAvailable, setCastAvailable] = useState(false);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  const channel = race?.channel || "Racing TV";
  const source = STREAM_SOURCES[channel] || STREAM_SOURCES["Racing TV"];

  // Check for casting support
  useEffect(() => {
    if (navigator.mediaDevices || window.chrome?.cast) {
      setCastAvailable(true);
    }
  }, []);

  // ESC key to close
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Prevent body scroll when player is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  const handleCast = useCallback(() => {
    // Use Web Share API as a casting proxy for mobile
    if (navigator.share) {
      navigator.share({
        title: `Watch ${race?.race_name} Live`,
        text: `${race?.track_name} - ${race?.time} on ${channel}`,
        url: source.embed,
      }).catch(() => {});
    } else {
      window.open(source.embed, "_blank");
    }
  }, [race, channel, source]);

  const openExternal = useCallback(() => {
    window.open(source.embed, "_blank");
  }, [source]);

  return (
    <div
      data-testid="live-stream-overlay"
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={containerRef}
        className={`relative w-full ${isFullscreen ? "h-full" : "max-w-[1100px] mx-4"} flex flex-col`}
      >
        {/* Top Bar - Race Info + Controls */}
        <div className="flex items-center justify-between bg-[#09090B] px-4 py-2.5 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 bg-[#E12B00] text-white px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest font-bold">
              <span className="w-2 h-2 bg-white rounded-full live-pulse" />
              Live
            </span>
            <div
              className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest font-bold text-white"
              style={{ backgroundColor: source.color }}
            >
              <Television weight="bold" size={12} className="inline mr-1" />
              {source.label}
            </div>
            {source.free && (
              <span className="bg-[#008A27] text-white px-2 py-0.5 text-[9px] font-mono font-bold uppercase">Free</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="stream-cast-btn"
              onClick={handleCast}
              className="p-1.5 text-zinc-400 hover:text-white transition-colors"
              title="Cast / Share"
            >
              <Screencast weight="bold" size={18} />
            </button>
            <button
              data-testid="stream-external-btn"
              onClick={openExternal}
              className="p-1.5 text-zinc-400 hover:text-white transition-colors"
              title="Open in new tab"
            >
              <ArrowSquareOut weight="bold" size={18} />
            </button>
            <button
              data-testid="stream-fullscreen-btn"
              onClick={toggleFullscreen}
              className="p-1.5 text-zinc-400 hover:text-white transition-colors"
              title="Fullscreen"
            >
              {isFullscreen ? <ArrowsIn weight="bold" size={18} /> : <ArrowsOut weight="bold" size={18} />}
            </button>
            <button
              data-testid="stream-close-btn"
              onClick={onClose}
              className="p-1.5 text-zinc-400 hover:text-red-400 transition-colors ml-1"
              title="Close"
            >
              <X weight="bold" size={20} />
            </button>
          </div>
        </div>

        {/* Video Area */}
        <div className="relative bg-black flex-1 min-h-[300px] md:min-h-[500px]">
          {/* Embedded Stream */}
          <iframe
            ref={iframeRef}
            src={source.embed}
            className="absolute inset-0 w-full h-full"
            allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
            allowFullScreen
            onLoad={() => setStreamLoaded(true)}
            onError={() => setStreamError(true)}
            style={{ border: "none" }}
          />

          {/* Loading overlay - shows fallback after 5s if iframe blocked */}
          {!streamLoaded && !streamError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black z-10">
              <div className="w-16 h-16 rounded-full bg-[#E12B00] flex items-center justify-center mb-4 animate-pulse">
                <Play weight="fill" size={28} className="text-white ml-0.5" />
              </div>
              <p className="text-white font-mono text-sm">Connecting to {source.label}...</p>
              <p className="text-zinc-500 font-mono text-xs mt-1">Stream may open in the embedded player or require an external tab</p>
              <button
                onClick={openExternal}
                className="mt-4 bg-white/10 backdrop-blur-sm text-white px-6 py-2.5 text-xs font-mono uppercase tracking-wider hover:bg-white/20 transition-colors flex items-center gap-2"
              >
                <ArrowSquareOut weight="bold" size={14} />
                Open {source.label} directly
              </button>
              {!source.free && (
                <p className="text-zinc-600 text-[10px] font-mono mt-2">Subscription to {source.label} may be required</p>
              )}
            </div>
          )}

          {/* Race Info Overlay (bottom-left) */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pointer-events-none z-[5]">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-white font-heading font-bold text-lg md:text-xl">{race?.race_name}</p>
                <div className="flex items-center gap-3 text-zinc-300 text-xs font-mono mt-1">
                  <span className="flex items-center gap-1"><MapPin weight="bold" size={12} />{race?.track_name}</span>
                  <span className="flex items-center gap-1"><Timer weight="bold" size={12} />{race?.time}</span>
                  <span>{race?.distance}</span>
                  {race?.prize && <span className="text-[#008A27] font-bold">{race?.prize}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 pointer-events-auto">
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="p-2 bg-black/50 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
                >
                  {isMuted ? <SpeakerSlash weight="bold" size={18} /> : <SpeakerHigh weight="bold" size={18} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom - Runner Quick View */}
        <div className="bg-[#09090B] px-4 py-2 border-t border-zinc-800 overflow-x-auto">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex-shrink-0">
              <Horse weight="bold" size={12} className="inline mr-1" />
              Runners
            </span>
            <div className="flex items-center gap-2 overflow-x-auto">
              {race?.runners?.slice(0, 8).map((r, i) => (
                <div
                  key={r.horse_id}
                  data-testid={`stream-runner-${i}`}
                  className={`flex-shrink-0 px-2 py-1 text-[10px] font-mono border ${
                    r.is_favourite ? "border-[#008A27] text-[#008A27] bg-green-950/30" :
                    r.is_most_backed ? "border-amber-500 text-amber-400 bg-amber-950/20" :
                    "border-zinc-700 text-zinc-400"
                  }`}
                >
                  <span className="font-bold">{r.horse_name}</span>
                  <span className="ml-1.5 opacity-70">{r.sp}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
